import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import PageMetaLoader from '../components/PageMetaLoader';

const pricingPlans = [
  {
    name: 'Standard Plan',
    description: 'Designed for SMEs looking for a reliable and localized HR & Payroll solution.',
    price: '$1',
    period: 'per employee / month',
    minimum: 'Minimum monthly fee: $50',
    setup: 'One-time setup fee: $300 - $500',
    highlight: false,
  },
  {
    name: 'Advanced Plan',
    description: 'Designed for growing businesses and enterprises requiring advanced HR operations and process automation.',
    price: '$2',
    period: 'per employee / month',
    minimum: 'Minimum monthly fee: $100',
    setup: 'One-time setup fee: $600 - $1,000',
    highlight: true,
  },
];

const standardFeatureGroups = [
  {
    title: 'Core HR',
    features: [
      'Personnel Management',
      'Employee Profile & Document Management',
      'Organizational Chart',
      'Multi-branch & Department Structure',
      'Multi-currency Support and exchange rate: Transactions, Salary Tax, NSSF',
    ],
  },
  {
    title: 'Workforce Management',
    features: [
      'Attendance Management',
      'Shift & Schedule Management',
      'Leave & Holiday Management',
      'Overtime Tracking',
    ],
  },
  {
    title: 'Payroll',
    features: [
      'Payroll Processing',
      'NSSF Contribution Management',
      'Pension Fund Calculation',
      'Allowance & Deduction Management',
      'Khmer & English Payslip',
    ],
  },
  {
    title: 'Workflow & Automation',
    features: [
      'Approval Workflows',
      'Notifications & Alerts',
      'Employee Self-Service',
    ],
  },
  {
    title: 'Reporting & Customization',
    features: [
      'Forms Customization',
      'Reports Customization',
      'Export to Excel/PDF',
    ],
  },
  {
    title: 'Standard Support',
    features: ['Included for free'],
  },
];

const advancedFeatureGroups = [
  {
    title: 'Talent Acquisition',
    features: [
      'Recruitment Management',
      'Job Vacancy Tracking',
      'Candidate Pipeline',
      'Interview Scheduling',
      'Applicant Database',
    ],
  },
  {
    title: 'Performance Management',
    features: [
      'Performance Review System',
      'KPI & Goal Tracking',
      'Manager & Employee Assessments - Multiple lines of reviewer (360)',
    ],
  },
  {
    title: 'Flex List (Dynamic Business Process Module)',
    description: 'Empower users to build their own operational workflows without development.',
    features: [
      'Custom List Builder',
      'Dynamic Form Creation',
      'Approval Flow Configuration',
      'Status Tracking',
      'Custom Fields',
      'Role-based Access',
      'Workflow Notifications',
    ],
    examples: [
      'Purchase Requests',
      'Asset Requests',
      'Travel Requests',
      'Expense Claims',
      'Internal Approval Processes',
    ],
  },
];

const standardSupportGroups = [
  {
    title: 'System Maintenance',
    features: [
      'Bug fixes',
      'Minor issue troubleshooting',
      'Performance monitoring',
      'Database maintenance',
      'Backup verification',
    ],
  },
  {
    title: 'User Support',
    features: [
      'Email support',
      'Telegram/Chat support during office hours',
      'Basic user guidance',
      'Password reset assistance',
      'User access troubleshooting',
      'Attendance sync troubleshooting',
    ],
  },
  {
    title: 'Functional Assistance',
    features: [
      'Payroll calculation issue checking',
      'Leave balance issue checking',
      'Attendance discrepancy review',
      'Report troubleshooting',
      'Basic workflow configuration help',
    ],
  },
  {
    title: 'Updates',
    features: [
      'Standard product updates',
      'Regulatory updates (Cambodia labor/payslip/tax related)',
      'Minor UI improvements',
      'Compatibility updates',
    ],
  },
];

const premiumSupportGroups = [
  {
    title: 'Priority Technical Support',
    features: [
      'Priority bug fixing queue',
      'Faster incident response',
      'Emergency support',
      'After-hours support',
      'Weekend support',
      'Hotline support',
      'Dedicated account manager',
    ],
  },
  {
    title: 'Advanced Troubleshooting',
    features: [
      'Payroll emergency handling',
      'Attendance device integration troubleshooting',
      'API troubleshooting',
      'Performance optimization',
      'Server resource analysis',
    ],
  },
  {
    title: 'Customization Support',
    features: [
      'Small feature enhancements',
      'Custom report creation',
      'Workflow adjustments',
      'Approval flow modifications',
      'Custom field configuration',
      'Template modifications',
      'HR policy configuration assistance',
    ],
  },
  {
    title: 'Business & Operational Support',
    features: [
      'Monthly system health review',
      'Usage analytics review',
      'HR process consultation',
      'Payroll process optimization',
      'Compliance advisory',
      'Best-practice recommendations',
    ],
  },
  {
    title: 'Advanced Operational Services',
    features: [
      'Disaster recovery assistance',
      'Priority backup restoration',
      'Data migration assistance',
      'Integration monitoring',
      'Server monitoring',
      'Scheduled maintenance coordination',
    ],
  },
];

const HRMSPricePage = () => {
  return (
    <PageSection>
      <PageMetaLoader pageUrl="products/hrms-price" />
      <div className="container">
        <HeroPanel>
          <Eyebrow>AKAS HRIS Pricing 2026</Eyebrow>
          <Title>HRMS Pricing Plans</Title>
          <Description>
            Choose the HR and payroll package that matches your team size, operational needs,
            and preferred support level.
          </Description>
          <ButtonRow>
            <PrimaryButton to="/contact">Contact Sales</PrimaryButton>
            <SecondaryButton to="/products/hrms">Back to HRMS</SecondaryButton>
          </ButtonRow>
        </HeroPanel>

        <PricingGrid>
          {pricingPlans.map((plan) => (
            <PricingCard key={plan.name} $highlight={plan.highlight}>
              {plan.highlight && <PopularBadge>Advanced</PopularBadge>}
              <PlanName>{plan.name}</PlanName>
              <PlanDescription>{plan.description}</PlanDescription>
              <PriceRow>
                <Price>{plan.price}</Price>
                <Period>{plan.period}</Period>
              </PriceRow>
              <PlanMeta>{plan.minimum}</PlanMeta>
              <PlanMeta>{plan.setup}</PlanMeta>
            </PricingCard>
          ))}
        </PricingGrid>

        <Section>
          <SectionHeader>
            <Eyebrow>Standard Plan</Eyebrow>
            <SectionTitle>Modules & Features</SectionTitle>
          </SectionHeader>
          <FeatureGrid>
            {standardFeatureGroups.map((group) => (
              <FeatureCard key={group.title}>
                <CardTitle>{group.title}</CardTitle>
                <FeatureList>
                  {group.features.map((feature) => (
                    <li key={feature}>{feature}</li>
                  ))}
                </FeatureList>
              </FeatureCard>
            ))}
          </FeatureGrid>
        </Section>

        <Section>
          <SectionHeader>
            <Eyebrow>Advanced Plan</Eyebrow>
            <SectionTitle>Everything in Standard, plus advanced operations</SectionTitle>
          </SectionHeader>
          <FeatureGrid>
            {advancedFeatureGroups.map((group) => (
              <FeatureCard key={group.title}>
                <CardTitle>{group.title}</CardTitle>
                {group.description && <CardDescription>{group.description}</CardDescription>}
                <FeatureList>
                  {group.features.map((feature) => (
                    <li key={feature}>{feature}</li>
                  ))}
                </FeatureList>
                {group.examples && (
                  <ExamplesBlock>
                    <ExamplesTitle>Examples</ExamplesTitle>
                    <FeatureList>
                      {group.examples.map((example) => (
                        <li key={example}>{example}</li>
                      ))}
                    </FeatureList>
                  </ExamplesBlock>
                )}
              </FeatureCard>
            ))}
          </FeatureGrid>
        </Section>

        <Section>
          <SectionHeader>
            <Eyebrow>Support</Eyebrow>
            <SectionTitle>Included Standard Support</SectionTitle>
          </SectionHeader>
          <FeatureGrid>
            {standardSupportGroups.map((group) => (
              <FeatureCard key={group.title}>
                <CardTitle>{group.title}</CardTitle>
                <FeatureList>
                  {group.features.map((feature) => (
                    <li key={feature}>{feature}</li>
                  ))}
                </FeatureList>
              </FeatureCard>
            ))}
          </FeatureGrid>
        </Section>

        <Section>
          <SupportPanel>
            <SectionHeader>
              <Eyebrow>Premium Support</Eyebrow>
              <SectionTitle>$150 - $250 per month</SectionTitle>
            </SectionHeader>
            <FeatureGrid>
              {premiumSupportGroups.map((group) => (
                <FeatureCard key={group.title}>
                  <CardTitle>{group.title}</CardTitle>
                  <FeatureList>
                    {group.features.map((feature) => (
                      <li key={feature}>{feature}</li>
                    ))}
                  </FeatureList>
                </FeatureCard>
              ))}
            </FeatureGrid>
          </SupportPanel>
        </Section>
      </div>
    </PageSection>
  );
};

const PageSection = styled.main`
  padding: 5.5rem 0 4.5rem;
  background: linear-gradient(180deg, #f5fbff 0%, #ffffff 64%);
`;

const HeroPanel = styled.section`
  border: 1px solid rgba(0, 142, 204, 0.16);
  border-radius: 18px;
  background: #fff;
  box-shadow: 0 16px 45px rgba(0, 0, 0, 0.05);
  padding: 2.4rem;
  margin-bottom: 1.5rem;

  @media (max-width: 768px) {
    padding: 1.6rem;
  }
`;

const Eyebrow = styled.span`
  display: inline-block;
  padding: 0.3rem 0.85rem;
  background: var(--primary-light);
  color: var(--primary-color);
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  margin-bottom: 0.9rem;
`;

const Title = styled.h1`
  color: #111827;
  font-size: 2.6rem;
  margin-bottom: 0.85rem;

  @media (max-width: 576px) {
    font-size: 2rem;
  }
`;

const Description = styled.p`
  max-width: 760px;
  color: #4b5563;
  line-height: 1.7;
  font-size: 1.05rem;
  margin: 0;
`;

const ButtonRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.8rem;
  margin-top: 1.5rem;
`;

const BaseButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  font-weight: 700;
  padding: 0.75rem 1.35rem;
  transition: transform 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease;

  &:hover {
    transform: translateY(-1px);
  }
`;

const PrimaryButton = styled(BaseButton)`
  background: var(--primary-color);
  color: #fff;

  &:hover {
    background: #0077ab;
    box-shadow: 0 10px 24px rgba(0, 142, 204, 0.24);
  }
`;

const SecondaryButton = styled(BaseButton)`
  background: #fff;
  color: var(--primary-color);
  border: 1px solid rgba(0, 142, 204, 0.28);
`;

const PricingGrid = styled.section`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
  margin-bottom: 2.5rem;

  @media (max-width: 820px) {
    grid-template-columns: 1fr;
  }
`;

const PricingCard = styled.article<{ $highlight: boolean }>`
  position: relative;
  border: 1px solid ${({ $highlight }) => ($highlight ? 'var(--primary-color)' : '#e5edf3')};
  border-radius: 16px;
  background: #fff;
  box-shadow: ${({ $highlight }) => ($highlight ? '0 16px 40px rgba(0, 142, 204, 0.12)' : 'none')};
  padding: 1.6rem;
`;

const PopularBadge = styled.span`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: var(--primary-color);
  color: #fff;
  border-radius: 999px;
  padding: 0.25rem 0.7rem;
  font-size: 0.76rem;
  font-weight: 700;
`;

const PlanName = styled.h2`
  color: #111827;
  font-size: 1.45rem;
  margin-bottom: 0.55rem;
`;

const PlanDescription = styled.p`
  color: #5b6572;
  line-height: 1.65;
  margin: 0 0 1.1rem;
`;

const PriceRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const Price = styled.span`
  color: var(--primary-color);
  font-size: 2.7rem;
  font-weight: 800;
`;

const Period = styled.span`
  color: #4b5563;
  font-weight: 600;
`;

const PlanMeta = styled.div`
  color: #374151;
  margin-top: 0.45rem;
`;

const Section = styled.section`
  margin-top: 2.5rem;
`;

const SectionHeader = styled.div`
  margin-bottom: 1.2rem;
`;

const SectionTitle = styled.h2`
  color: #111827;
  font-size: 1.9rem;
  margin-bottom: 0;

  @media (max-width: 576px) {
    font-size: 1.5rem;
  }
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1rem;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 680px) {
    grid-template-columns: 1fr;
  }
`;

const FeatureCard = styled.article`
  border: 1px solid #e5edf3;
  border-radius: 14px;
  background: #fff;
  padding: 1.25rem;
`;

const CardTitle = styled.h3`
  color: var(--primary-color);
  font-size: 1.12rem;
  margin-bottom: 0.65rem;
`;

const CardDescription = styled.p`
  color: #5b6572;
  line-height: 1.6;
  margin: 0 0 0.8rem;
`;

const FeatureList = styled.ul`
  display: grid;
  gap: 0.45rem;
  padding-left: 1.15rem;
  margin: 0;
  color: #374151;
  line-height: 1.55;
`;

const ExamplesBlock = styled.div`
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #edf2f7;
`;

const ExamplesTitle = styled.h4`
  color: #111827;
  margin-bottom: 0.55rem;
`;

const SupportPanel = styled.div`
  border-radius: 18px;
  padding: 1.6rem;
  background: linear-gradient(180deg, rgba(0, 142, 204, 0.08), rgba(255, 255, 255, 1));
  border: 1px solid rgba(0, 142, 204, 0.16);
`;

export default HRMSPricePage;
